import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { animate, stagger } from "animejs";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ChatMessageAttachments,
  MessageBubbleAttachmentActionsProvider,
} from "@/components/chat/chat-message-attachments";
import { MessageBodyMarkdown } from "@/components/messages/message-body-markdown";
import {
  MessageReceiptsDetail,
  MessageReceiptsPopover,
} from "@/components/messages/message-receipts-popover";
import { readChannelStatus } from "@/lib/messages/message-delivery-status";
import {
  buildWhatsAppMessageIdIndex,
  formatCallDuration,
  formatReceiptTime,
  getMessageText,
  getMessageTime,
  getWhatsAppDeleteError,
  getWhatsAppQuotedMessageId,
  isWhatsAppDeleteFailed,
  isWhatsAppDeletePending,
  isWhatsAppDeletedMessage,
  isWhatsAppStaffAnswerMessage,
} from "@/lib/messages/messages-helpers";
import {
  canEditInternalMessage,
  canEditWhatsAppMessage,
  canUnsendInternalMessage,
  getEditedInfo,
  getMessageDisplayText,
  getWhatsAppEditError,
  isWhatsAppEditFailed,
  isWhatsAppEditPending,
} from "@/lib/messages/message-edit-helpers";
import { getCallLogDetail, getMessageDedupKey } from "@/lib/realtime/chat-message-helpers";
import { MAX_FORWARD_SELECTION, getForwardedInfo } from "@/lib/messages/forward-message";
import { requestOpenThreadMessage } from "@/lib/notifications/notification-navigation";
import { captureElementAsPngBlob } from "@/lib/clipboard/capture-element-image";
import { copyImageBlobToClipboard } from "@/lib/clipboard/image-clipboard";
import {
  Ban,
  Check,
  ChevronLeft,
  Copy,
  Eraser,
  Forward,
  Info,
  ListChecks,
  MoreHorizontal,
  Pencil,
  Phone,
  PhoneIncoming,
  PhoneMissed,
  PhoneOff,
  PhoneOutgoing,
  Pin,
  SmilePlus,
  Square,
  Trash2,
  UserRound,
  Video,
  X,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  REACTION_QUICK_SET,
  businessReaction,
  groupReactionsByEmoji,
  patientReaction,
} from "@/lib/messages/reactions-helpers";
import { getAvatarColorClasses, getSenderNameColorClasses } from "@/lib/messages/avatar-color";
// DISABLED (for now): the staff_handles catalog entry is retired — use the person
// glyph directly for "Staff answered" avatars instead of looking it up.
// import { getFlagDefinition } from "@/lib/thread-flags";
import { tenantColorVars } from "@/lib/messages/merged-thread-colors";
import { cn } from "@/lib/utils";

// const StaffHandlesGlyphIcon = getFlagDefinition("staff_handles")?.icon ?? UserRound;
const StaffHandlesGlyphIcon = UserRound;

const MAX_STAGGER_BUBBLES = 25;

/**
 * What to animate for a newly-arrived message. A lone first message animates as its whole bubble
 * row (so the avatar rides along); a message merged into an existing same-sender bubble animates
 * just its own segment, leaving the older segments in place. Auto/system lines carry the key on the
 * bubble element itself.
 */
function entranceAnimationTarget(el) {
  if (el.hasAttribute("data-msg-bubble")) return el;
  const bubble = el.closest("[data-msg-bubble]");
  if (!bubble) return el;
  // A lone first message animates as its whole row (avatar + header ride along); a merged segment
  // animates alone so the older segments above it stay put.
  return bubble.querySelectorAll("[data-part-key]").length <= 1 ? bubble : el;
}

/** Transient ring shown on a message jumped to from a clicked notification. */
const HIGHLIGHT_RING_CLASS =
  "rounded-lg ring-2 ring-amber-400/80 ring-offset-2 ring-offset-background transition-shadow";

// A message ticked for forwarding. Primary rather than the jump highlight's amber, and on the
// PART — a merged bubble can have one message in the batch and the next one out of it.
const FORWARD_SELECTED_PART_CLASS =
  "rounded-lg ring-2 ring-primary ring-offset-2 ring-offset-background";

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      <div className="flex items-end gap-2">
        <Skeleton className="h-7 w-7 rounded-full" />
        <Skeleton className="h-20 w-[75%] rounded-xl" />
      </div>
      <div className="flex items-end justify-end gap-2">
        <Skeleton className="h-24 w-[55%] rounded-xl" />
        <Skeleton className="h-7 w-7 rounded-full" />
      </div>
      <div className="flex items-end gap-2">
        <Skeleton className="h-7 w-7 rounded-full" />
        <Skeleton className="h-16 w-[65%] rounded-xl" />
      </div>
    </div>
  );
}

/**
 * WhatsApp-style quoted-message block shown INSIDE a bubble, above the reply text —
 * mirrors how WhatsApp renders a native reply-to (`metadata.whatsapp.quotedMessageId`).
 * healo_bot emits these when its reply raced with newer patient messages: the reply
 * quotes the message it actually answers, and the raced message is intentionally left
 * unanswered — this block is what makes that visible to staff. Clicking jumps to the
 * original message when it is present in the loaded history.
 */
function QuotedMessageBlock({ quotedMessage, getSenderLabel, isBotSender, onJumpToMessage }) {
  const resolved = Boolean(quotedMessage);
  const quotedBot = resolved && isBotSender ? isBotSender(quotedMessage) : false;
  const label = resolved
    ? (quotedBot ? "Bot" : (getSenderLabel ? getSenderLabel(quotedMessage) : "")) || "Original message"
    : "Original message";
  const text = resolved ? getMessageText(quotedMessage) : "(original message unavailable)";
  const targetId = resolved && quotedMessage.id ? String(quotedMessage.id) : "";
  const clickable = Boolean(targetId && onJumpToMessage);

  const body = (
    <>
      <p
        className={`truncate text-xs font-semibold ${quotedBot ? "text-green-600 dark:text-green-400" : "text-primary"}`}
      >
        {label}
      </p>
      <p
        className={cn(
          "line-clamp-2 wrap-break-word text-xs text-muted-foreground",
          resolved && isWhatsAppDeletedMessage(quotedMessage) && "line-through opacity-70",
        )}
      >
        {text}
      </p>
    </>
  );

  const skin = `mb-2 w-full rounded-md border-l-4 bubble-surface-muted px-2.5 py-1.5 text-start ${
    quotedBot ? "border-green-500/70" : "border-primary/70"
  }`;

  if (!clickable) {
    return <div className={skin}>{body}</div>;
  }
  return (
    <button
      type="button"
      onClick={() => onJumpToMessage(targetId)}
      title="Go to the quoted message"
      className={`${skin} block cursor-pointer transition hover:bg-muted/80 dark:hover:bg-muted/50`}
    >
      {body}
    </button>
  );
}

/**
 * Provenance line on a forwarded message (`metadata.forwarded`, see lib/messages/forward-message).
 * Shown in internal chats ONLY — a patient conversation renders the forwarded copy as a plain
 * message with no marker (the caller gates on scope). A patient-sourced forward is the clickable
 * variant, "Forwarded from <patient>", jumping to the original conversation point via the
 * cross-pane navigation bridge (scope "normal": the source always lives in the Messages pane).
 * An internal-chat source stays a plain "Forwarded" label, deliberately not clickable.
 */
function ForwardedMarkerBlock({ info }) {
  const clickable = info.sourceScope === "patient" && info.sourceThreadId && info.sourceMessageId;
  const label = clickable && info.sourceTitle ? `Forwarded from ${info.sourceTitle}` : "Forwarded";
  const inner = (
    <>
      <Forward className="h-3 w-3 shrink-0" aria-hidden />
      <span className="truncate">{label}</span>
    </>
  );
  if (!clickable) {
    return (
      <div className="mb-1 flex max-w-full items-center gap-1 text-[11px] italic text-muted-foreground">
        {inner}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() =>
        requestOpenThreadMessage({
          threadId: info.sourceThreadId,
          messageId: info.sourceMessageId,
          scope: "normal",
        })
      }
      title="Go to the original conversation"
      className="mb-1 flex max-w-full cursor-pointer items-center gap-1 text-[11px] italic text-muted-foreground transition hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {inner}
    </button>
  );
}

/**
 * Reaction chips shown under a message: the patient's reaction (display-only) and the shared
 * business reaction (click to clear). Renders nothing when there are no reactions.
 *
 * WhatsApp GROUP threads (`isWaGroupThread`) switch to one chip PER EMOJI with a count — a group
 * message can hold many participant slots plus the shared business slot. Clicking any chip toggles
 * the TEAM's reaction to that emoji (the only slot we own; participant slots are theirs), and the
 * chip highlights when the business slot holds it. The chip's tooltip names the reactors:
 * `senderJid` → roster name, staff `byUserId` → member directory, else the jid's phone digits.
 */
function MessageReactions({
  message,
  threadId,
  onToggleReaction,
  rightAligned,
  isInternalScope,
  isWaGroupThread = false,
  waGroupNamesByJid = null,
  memberDirectory = null,
}) {
  const messageId = message?.id ? String(message.id) : "";
  const tid = String(threadId || message?.threadId || "").trim();
  const biz = businessReaction(message);
  const patient = patientReaction(message);
  const canReact = Boolean(onToggleReaction && tid && messageId);

  if (isWaGroupThread) {
    const grouped = groupReactionsByEmoji(message);
    if (grouped.length === 0) {
      return null;
    }
    const reactorName = (r) => {
      const jid = String(r?.senderJid || "").trim().toLowerCase();
      if (jid && waGroupNamesByJid?.[jid]) return waGroupNamesByJid[jid];
      const uid = String(r?.byUserId || "").trim();
      if (uid && memberDirectory?.[uid]?.label) return memberDirectory[uid].label;
      if (jid) return jid.split("@")[0];
      return r?.party === "business" ? "Staff" : "Someone";
    };
    return (
      <div
        className={`mt-1.5 flex flex-wrap items-center gap-1 ${rightAligned ? "justify-end" : "justify-start"}`}
      >
        {grouped.map((g) => (
          <button
            key={g.emoji}
            type="button"
            onClick={() => canReact && onToggleReaction(tid, messageId, g.emoji)}
            disabled={!canReact}
            className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-sm leading-none shadow-sm transition disabled:cursor-default disabled:opacity-100 ${
              g.mine
                ? "border-primary/40 bg-primary/10 hover:bg-primary/20"
                : "border-border/60 bg-background/80 hover:bg-muted/80"
            }`}
            title={`${g.reactors.map(reactorName).join(", ")}${
              g.mine ? " — click to remove your team's reaction" : " — click to react too"
            }`}
          >
            {g.emoji}
            {g.count > 1 ? (
              <span className="text-[11px] font-semibold text-muted-foreground">{g.count}</span>
            ) : null}
          </button>
        ))}
      </div>
    );
  }

  if (!biz && !patient) {
    return null;
  }

  return (
    <div
      className={`mt-1.5 flex flex-wrap items-center gap-1 ${rightAligned ? "justify-end" : "justify-start"}`}
    >
      {patient ? (
        <span
          className="inline-flex items-center rounded-full border border-border/60 bg-background/80 px-1.5 py-0.5 text-sm leading-none shadow-sm"
          title={isInternalScope ? "Reaction" : "Patient reaction"}
        >
          {patient.emoji}
        </span>
      ) : null}
      {biz ? (
        <button
          type="button"
          onClick={() => canReact && onToggleReaction(tid, messageId, biz.emoji)}
          disabled={!canReact}
          className="inline-flex items-center rounded-full border border-primary/40 bg-primary/10 px-1.5 py-0.5 text-sm leading-none shadow-sm transition hover:bg-primary/20 disabled:cursor-default disabled:opacity-100"
          title="Your team's reaction — click to remove"
        >
          {biz.emoji}
        </button>
      ) : null}
    </div>
  );
}

/** One labelled row in a message's actions menu. */
function MessageMenuItem({ icon: Icon, label, onSelect, tone = "default" }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
      className={cn(
        "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition",
        tone === "destructive"
          ? "text-destructive hover:bg-destructive/10"
          : tone === "restricted"
            ? "text-amber-600 hover:bg-amber-500/10 dark:text-amber-300"
            : "text-foreground hover:bg-muted",
      )}
    >
      <Icon className="h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden />
      <span className="min-w-0 truncate">{label}</span>
    </button>
  );
}

/**
 * Hover chip for ONE message: a quick-reaction button and a "⋯" menu holding everything else
 * (message info, forward, edit, delete for everyone, and the DEV server tools under their own
 * heading). "Delete for everyone" and "Edit message" branch by `actionScope`: on WhatsApp they are
 * the revoke and the 15-minute edit of a message our side sent; in internal Chats they are the
 * author's own unsend/edit (no window). The page decides WHICH handler runs; this chip only decides
 * whether the item is offered for THIS message.
 *
 * Per MESSAGE, not per bubble: consecutive sends from the same sender merge into one bubble with no
 * time window, so a per-bubble strip could only ever act on the cluster's LAST message — you would
 * react to, forward, or delete something other than the message you are pointing at.
 *
 * Two constraints shaped this. It cannot live OUTSIDE the bubble: a bubble takes up to 92% of the
 * row (`max-w-[min(92%,44rem)]`), so the ~8% left over fits one small button, not a row of them —
 * anything wider was silently clipped. And it cannot be permanent: a control under every message
 * turns the transcript into furniture. So it is two 20px glyphs, on hover only, straddling the
 * message's bottom-right corner where they cost no layout and cover nothing at rest — the end of the
 * message you are pointing at. Everything destructive or rare sits behind the menu, in words rather
 * than glyphs. (The bubble has no `overflow-hidden`, so the half that hangs past the edge shows.)
 *
 * `data-capture-ignore` keeps it out of copy-as-image captures.
 */
function MessagePartActions({
  message,
  threadId,
  onToggleReaction,
  onForwardMessage,
  // Enters the multi-select forward mode with this message already ticked.
  onStartForwardSelection,
  onRevokeMessage,
  onEditMessage,
  // "whatsapp" | "internal" | "" — which rules gate Delete for everyone / Edit message.
  actionScope = "",
  onDeleteMessage,
  onClearMessagesAfter,
  isOwn,
  recipientIds,
  primaryRecipientIds,
  isWhatsAppThread,
  memberDirectory,
  isWaGroupThread,
  waGroupNamesByJid,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  // "Message info" swaps the menu for the receipts panel in place: a popover opened from inside a
  // popover would be dismissed by Radix the moment it was touched.
  const [showInfo, setShowInfo] = useState(false);
  // WhatsApp: a message already deleted on WhatsApp has nothing left to retract. Internal: only a
  // real, server-acknowledged conversational row (system lines and optimistic placeholders never).
  const canRevoke =
    Boolean(onRevokeMessage) &&
    (actionScope === "whatsapp" ? !isWhatsAppDeletedMessage(message) : canUnsendInternalMessage(message));
  // WhatsApp: text-only, ours, not deleted, no edit in flight, and inside WhatsApp's 15-minute edit
  // window (measured at render; the hook re-checks at save and the server is the authority).
  // Internal: own text message, no window.
  const canEdit =
    Boolean(onEditMessage) &&
    (actionScope === "whatsapp"
      ? canEditWhatsAppMessage(message, { nowMs: Date.now() })
      : canEditInternalMessage(message));
  const showDevActions = Boolean(import.meta.env.DEV && (onDeleteMessage || onClearMessagesAfter));
  const messageId = message?.id ? String(message.id) : "";
  const tid = String(threadId || message?.threadId || "").trim();
  if (!tid || !messageId) return null;
  // The centered system / auto-reply / call notices carry no conversation actions, and "message
  // info" alone does not earn a chip — they get nothing.
  if (!onToggleReaction && !onForwardMessage && !canRevoke && !canEdit && !showDevActions) return null;

  const closeMenu = () => {
    setMenuOpen(false);
    setShowInfo(false);
  };

  return (
    <div
      data-capture-ignore="true"
      className={cn(
        "absolute bottom-0 right-0 z-20 flex translate-y-1/2 items-center gap-0.5 rounded-full",
        "border border-border/60 bg-card/95 px-1 py-0.5 shadow-sm backdrop-blur-sm",
        "opacity-0 transition-opacity focus-within:opacity-100 group-hover/part:opacity-100",
        // An open menu pins the chip: the pointer has to leave the message to reach it.
        menuOpen && "opacity-100",
      )}
    >
      <AddReactionButton message={message} threadId={tid} onToggleReaction={onToggleReaction} />
      <Popover
        open={menuOpen}
        onOpenChange={(next) => {
          setMenuOpen(next);
          if (!next) setShowInfo(false);
        }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            data-capture-ignore="true"
            aria-label="Message actions"
            title="More actions"
            className={messageActionButtonClassName}
          >
            <MoreHorizontal className="h-3.5 w-3.5" aria-hidden />
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" sideOffset={6} className="w-60 p-1" data-capture-ignore="true">
          {showInfo ? (
            <>
              <button
                type="button"
                onClick={() => setShowInfo(false)}
                className="flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-xs text-muted-foreground transition hover:bg-muted"
              >
                <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
                Back
              </button>
              <div className="-mx-1 border-t border-border/60 pt-1">
                <MessageReceiptsDetail
                  message={message}
                  isOwn={isOwn}
                  recipientIds={recipientIds}
                  primaryRecipientIds={primaryRecipientIds}
                  isWhatsAppThread={isWhatsAppThread}
                  memberDirectory={memberDirectory}
                  isWaGroupThread={isWaGroupThread}
                  waGroupNamesByJid={waGroupNamesByJid}
                  showTitle={false}
                />
              </div>
            </>
          ) : (
            <>
              <MessageMenuItem icon={Info} label="Message info" onSelect={() => setShowInfo(true)} />
              {onForwardMessage ? (
                <MessageMenuItem
                  icon={Forward}
                  label="Forward…"
                  onSelect={() => {
                    closeMenu();
                    onForwardMessage(message);
                  }}
                />
              ) : null}
              {onStartForwardSelection ? (
                <MessageMenuItem
                  icon={ListChecks}
                  label="Select messages…"
                  onSelect={() => {
                    closeMenu();
                    onStartForwardSelection(message);
                  }}
                />
              ) : null}
              {canEdit ? (
                <MessageMenuItem
                  icon={Pencil}
                  label="Edit message"
                  onSelect={() => {
                    closeMenu();
                    onEditMessage(message);
                  }}
                />
              ) : null}
              {canRevoke ? (
                <MessageMenuItem
                  icon={Ban}
                  label="Delete for everyone"
                  tone="destructive"
                  onSelect={() => {
                    closeMenu();
                    onRevokeMessage(message);
                  }}
                />
              ) : null}
              {showDevActions ? (
                // Its own heading, in the restricted amber: everything below is build-time tooling,
                // not something that touches the patient's conversation.
                <div className="mt-1 border-t border-border/60 px-2 pb-0.5 pt-1.5 text-[10px] font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-300">
                  Developer tools
                </div>
              ) : null}
              {showDevActions && onDeleteMessage ? (
                <MessageMenuItem
                  icon={Trash2}
                  label="Delete message (DEV)"
                  tone="restricted"
                  onSelect={() => {
                    closeMenu();
                    if (window.confirm("Delete this message for everyone? (DEV — soft delete, stays in the DB)")) {
                      onDeleteMessage(tid, messageId);
                    }
                  }}
                />
              ) : null}
              {showDevActions && onClearMessagesAfter ? (
                <MessageMenuItem
                  icon={Eraser}
                  label="Clear to this point (DEV)"
                  tone="restricted"
                  onSelect={() => {
                    closeMenu();
                    if (
                      window.confirm(
                        "Clear the conversation to this point? Every message AFTER this one is deleted for everyone (DEV — soft delete); this message stays as the last one.",
                      )
                    ) {
                      onClearMessagesAfter(tid, messageId);
                    }
                  }}
                />
              ) : null}
            </>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}

/**
 * The footer line under a struck-through bubble, saying what actually happened to it on WhatsApp.
 * Three states, and the distinction is the whole point of the feature: a retraction WE asked for
 * may be refused (past WhatsApp's ~2-day window, session down), and staff must not be left thinking
 * a message was pulled back while the patient still has it.
 *   - pending — we asked, WhatsApp has not answered yet
 *   - failed  — WhatsApp refused; the patient still sees it (destructive, with the reason on hover)
 *   - done    — gone from the patient's phone (also the inbound case: the patient deleted it)
 */
function WhatsAppDeletedCue({ message, className }) {
  if (isWhatsAppDeleteFailed(message)) {
    const reason = getWhatsAppDeleteError(message);
    return (
      <div className={cn("mt-1 text-[11px] font-medium text-destructive", className)}>
        Couldn't delete for the patient — they can still see this
        {/* The reason is shown, not tucked into a tooltip: it is the difference between "WhatsApp
            refused because the message is too old" (nothing to do) and a broken bridge (fix it). */}
        {reason ? <span className="block font-normal opacity-80">{reason}</span> : null}
      </div>
    );
  }
  if (isWhatsAppDeletePending(message)) {
    return (
      <div className={cn("mt-1 text-[11px] italic text-muted-foreground", className)}>
        Deleting for everyone…
      </div>
    );
  }
  return (
    <div className={cn("mt-1 text-[11px] italic text-muted-foreground", className)}>
      Deleted on WhatsApp
    </div>
  );
}

/**
 * "edited" beside the time of a message whose text was changed after sending. The tooltip carries
 * WHEN for the server stamp; an inbound patient edit relayed by the WhatsApp gateway may carry no
 * reliable time, so it reads just "Edited".
 */
function EditedMarker({ message }) {
  const info = getEditedInfo(message);
  if (!info) return null;
  const when = info.at ? formatReceiptTime(info.at) : "";
  return (
    <span className="text-[11px] italic text-muted-foreground" title={when ? `Edited ${when}` : "Edited"}>
      edited
    </span>
  );
}

/**
 * Outcome line for an outbound WhatsApp edit WE requested: pending (the bubble already shows the
 * new text, WhatsApp has not confirmed yet) or refused (the patient still sees the original — the
 * distinction staff must not miss). Silent once settled: the "edited" marker takes over.
 */
function WhatsAppEditCue({ message, className }) {
  if (isWhatsAppEditFailed(message)) {
    const reason = getWhatsAppEditError(message);
    return (
      <div className={cn("mt-1 text-[11px] font-medium text-destructive", className)}>
        Couldn&apos;t edit for the patient — they still see the original
        {reason ? <span className="block font-normal opacity-80">{reason}</span> : null}
      </div>
    );
  }
  if (isWhatsAppEditPending(message)) {
    return (
      <div className={cn("mt-1 text-[11px] italic text-muted-foreground", className)}>
        Editing on WhatsApp…
      </div>
    );
  }
  return null;
}

const gutterActionButtonClassName =
  "inline-flex h-7 w-7 items-center justify-center rounded-full border border-border/60 bg-background text-muted-foreground shadow-sm transition hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

/**
 * Buttons in a message's hover chip. Borderless and 20px — the chip itself supplies the frame, and
 * two of these must sit over a message corner without looking like a toolbar bolted to it.
 */
const messageActionButtonClassName =
  "inline-flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground transition hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

/**
 * Toggle a bubble into the multi-select copy set (does not copy yet).
 * Selected = filled check; idle = copy icon. Parent owns selection + final capture.
 * @param {{ bubbleKey: string, selected: boolean, selecting: boolean, onToggle: (key: string) => void }} props
 */
function CopyMessageAsImageButton({ bubbleKey, selected, selecting, onToggle }) {
  return (
    <button
      type="button"
      data-capture-ignore="true"
      aria-label={selected ? "Deselect message" : selecting ? "Select message" : "Select messages to copy as image"}
      aria-pressed={selected}
      title={
        selecting
          ? selected
            ? "Deselect"
            : "Select"
          : "Select messages to copy as image"
      }
      onClick={(e) => {
        e.stopPropagation();
        onToggle?.(bubbleKey);
      }}
      className={cn(
        gutterActionButtonClassName,
        selected && "border-primary/50 bg-primary/15 text-primary",
      )}
    >
      {selected ? <Check className="h-3.5 w-3.5" aria-hidden /> : <Copy className="h-3.5 w-3.5" aria-hidden />}
    </button>
  );
}

/**
 * The forward-selection tick for one message. It parks in the exact slot MessagePartActions
 * vacates — same chip chrome, same corner — because the offsets around a bubble are hand-tuned
 * and documented as needing to stay in sync; reusing the slot beats inventing a second geometry.
 * Always visible while selecting: the ticks have to be easy to hit, not hover-hunted.
 * Renders nothing for an optimistic send — a message with no server id cannot be forwarded.
 */
function ForwardSelectPartButton({ message, partKey, selected, onToggle }) {
  if (!message?.id) return null;
  return (
    <div
      data-capture-ignore="true"
      className={cn(
        "absolute bottom-0 right-0 z-20 flex translate-y-1/2 items-center rounded-full",
        "border border-border/60 bg-card/95 px-1 py-0.5 shadow-sm backdrop-blur-sm",
        selected && "border-primary/50",
      )}
    >
      <button
        type="button"
        data-capture-ignore="true"
        aria-label={selected ? "Deselect message" : "Select message"}
        aria-pressed={selected}
        title={selected ? "Deselect" : "Select"}
        onClick={(event) => {
          event.stopPropagation();
          onToggle?.(partKey);
        }}
        className={cn(messageActionButtonClassName, selected && "bg-primary/15 text-primary")}
      >
        {selected ? (
          <Check className="h-3.5 w-3.5" aria-hidden />
        ) : (
          <Square className="h-3.5 w-3.5" aria-hidden />
        )}
      </button>
    </div>
  );
}

/**
 * "Add reaction" affordance shown in the gutter beside a message bubble on hover (anchored to the
 * bubble's avatar-opposite side). Opens the fixed WhatsApp quick set; picking an emoji sets/replaces
 * the shared business reaction, and re-picking the active one clears it. Targets the given message.
 * Positioning is owned by MessageBubbleGutterActions — this button is in-flow.
 */
function AddReactionButton({ message, threadId, onToggleReaction }) {
  const [open, setOpen] = useState(false);
  const messageId = message?.id ? String(message.id) : "";
  const tid = String(threadId || message?.threadId || "").trim();
  if (!onToggleReaction || !tid || !messageId) {
    return null;
  }
  const biz = businessReaction(message);
  const pick = (emoji) => {
    setOpen(false);
    onToggleReaction(tid, messageId, emoji);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-capture-ignore="true"
          aria-label="Add reaction"
          className={gutterActionButtonClassName}
        >
          <SmilePlus className="h-4 w-4" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent align="center" sideOffset={6} className="w-auto p-1" data-capture-ignore="true">
        <div className="flex items-center gap-0.5">
          {REACTION_QUICK_SET.map((emoji) => (
            <button
              key={emoji}
              type="button"
              onClick={() => pick(emoji)}
              aria-label={`React ${emoji}`}
              className={`flex h-8 w-8 items-center justify-center rounded-md text-lg leading-none transition hover:bg-muted ${biz?.emoji === emoji ? "bg-primary/15" : ""}`}
            >
              {emoji}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * "Forward" affordance in the gutter: hands the message up so the page can open the forward
 * dialog (pick a staff chat or a patient, then send a clean copy with a provenance stamp).
 * Hidden while the message has no server id yet (optimistic sends can't be forwarded).
 * Positioning is owned by MessageBubbleGutterActions — this button is in-flow.
 */
function ForwardMessageButton({ message, onForwardMessage }) {
  const messageId = message?.id ? String(message.id) : "";
  if (!onForwardMessage || !messageId) {
    return null;
  }
  return (
    <button
      type="button"
      data-capture-ignore="true"
      aria-label="Forward message"
      title="Forward — send a copy to staff or a patient"
      onClick={(e) => {
        e.stopPropagation();
        onForwardMessage(message);
      }}
      className={gutterActionButtonClassName}
    >
      <Forward className="h-4 w-4" aria-hidden />
    </button>
  );
}

/**
 * Hover gutter on the avatar-opposite side of a bubble — the one action that is genuinely
 * bubble-scoped: copy-as-image captures the whole bubble (`data-copy-bubble-key`), and while
 * multi-select is active it stays visible so the checks remain easy to hit. Everything that targets
 * a single message lives in MessagePartActions instead.
 *
 * Geometry note: one h-7 (1.75rem) button starting 0.5rem from the bubble edge reaches +2.25rem, so
 * MessagePartActions parks at +3.5rem from the part edge (= +2.5rem from the bubble, since the part
 * sits 1rem inside via px-4) — keep the two in sync when adding buttons here.
 * @param {{ side: 'left' | 'right', bubbleKey: string, copySelected: boolean, copySelecting: boolean, onToggleCopySelect: (key: string) => void }} props
 */
function MessageBubbleGutterActions({ side, bubbleKey, copySelected, copySelecting, onToggleCopySelect }) {
  const positionClassName =
    side === "left"
      ? "left-0 -translate-x-[calc(100%+0.5rem)] flex-row-reverse"
      : "right-0 translate-x-[calc(100%+0.5rem)]";

  return (
    <div
      data-capture-ignore="true"
      className={cn(
        "absolute top-1/2 z-10 flex -translate-y-1/2 items-center gap-1 opacity-0 transition focus-within:opacity-100 group-hover/bubble:opacity-100",
        copySelecting && "opacity-100",
        positionClassName,
      )}
    >
      <CopyMessageAsImageButton
        bubbleKey={bubbleKey}
        selected={copySelected}
        selecting={copySelecting}
        onToggle={onToggleCopySelect}
      />
    </div>
  );
}

/**
 * Local "pin this message" toggle, anchored to the bubble's TOP-RIGHT corner — rendered in the
 * header row for labelled bubbles (staff/bot/other) and floated into the corner for compact
 * own-message bubbles. Pins are private and client-only — they feed the Messages "Pinned" lens.
 * A pinned bubble keeps the button visible (filled amber pin); otherwise it reveals on bubble hover.
 * `onTogglePin(message)` hands the message up so the page can snapshot its preview/sender/time.
 * Renders nothing when pinning is unavailable (e.g. internal Chats scope) or the message has no id.
 * `className` lets each layout place it (corner float vs. in-flow header chip).
 */
function PinMessageButton({ message, isPinned, onTogglePin, className }) {
  const messageId = message?.id ? String(message.id) : "";
  if (!onTogglePin || !messageId) return null;
  return (
    <button
      type="button"
      data-capture-ignore="true"
      aria-label={isPinned ? "Unpin message" : "Pin message"}
      aria-pressed={isPinned}
      title={isPinned ? "Unpin — remove from your Pinned list" : "Pin — save to your Pinned list"}
      onClick={(e) => {
        e.stopPropagation();
        onTogglePin(message);
      }}
      className={cn(
        "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        isPinned
          ? "border-amber-500/50 bg-amber-100 text-amber-600 dark:border-amber-400/40 dark:bg-amber-500/20 dark:text-amber-300"
          : "border-border/60 bg-background text-muted-foreground opacity-0 hover:text-foreground focus-visible:opacity-100 group-hover/bubble:opacity-100",
        className,
      )}
    >
      <Pin className={cn("h-3.5 w-3.5", isPinned && "fill-current")} aria-hidden />
    </button>
  );
}

/**
 * @param {object} props
 * @param {boolean} props.historyLoading
 * @param {string} props.historyError
 * @param {import("react").RefObject<HTMLDivElement | null>} props.historyEndRef
 * @param {Array} props.messageClusters
 * @param {(m: object) => boolean} props.isOwnMessage
 * @param {(m: object) => boolean} props.isBotSender
 * @param {(m: object) => string} props.getSenderLabel
 * @param {(m: object) => string} props.getSenderInitials
 * @param {(m: object) => string} props.getSenderAvatarUrl
 * @param {string} [props.selectedThreadId]
 * @param {string} [props.patientUserId] The thread patient's user id — used to tell a teammate's
 *   left-aligned reply apart from the patient's own inbound messages.
 * @param {(threadId: string, messageId: string, emoji: string) => void} [props.onToggleReaction]
 * @param {(message: object) => void} [props.onForwardMessage] Open the forward dialog for this
 *   message; omit to hide the forward affordance.
 * @param {(message: object) => void} [props.onRevokeMessage] "Delete for everyone" — WhatsApp
 *   revoke or internal unsend, chosen by the page; omit to hide the affordance.
 * @param {(message: object) => void} [props.onEditMessage] "Edit message" (WhatsApp within 15
 *   minutes, internal own messages); omit to hide the affordance.
 * @param {Set<string>} [props.pinnedMessageIds] Ids of messages the user has locally pinned (this thread).
 * @param {(message: object) => void} [props.onTogglePin] Toggle a local pin; omit to hide the pin affordance.
 * @param {(threadId: string, messageId: string) => void} [props.onDeleteMessage] DEV-only soft delete.
 * @param {(threadId: string, messageId: string) => void} [props.onClearMessagesAfter] DEV-only "clear to this point".
 * @param {string} [props.highlightedMessageId] Message to briefly ring-highlight (e.g. jumped to from a notification).
 * @param {boolean} [props.mergedView] Render the merged WhatsApp view — group consecutive clusters
 *   by their source clinic (`cluster.threadId`/`cluster.botId`) into gutter-rail segments.
 * @param {(botId: string) => ({ shortName?: string, label?: string } | null)} [props.getSegmentMeta]
 *   Resolve a cluster's clinic marking (short name / full label) from its `botId`. Merged view only.
 * @param {string[] | null} [props.recipientIds] Every OTHER thread member, for the delivery ticks;
 *   null while the roster is loading.
 * @param {boolean} [props.isWhatsAppThread] Use the channel-level WhatsApp status instead of
 *   per-recipient receipts.
 * @param {Record<string, {label?: string, avatarUrl?: string}> | null} [props.memberDirectory]
 *   Id → display name/avatar for the receipt detail popover.
 * @param {boolean} [props.isWaGroupThread] The open thread is a bridged WhatsApp GROUP: per-sender
 *   name colors, no "STAFF ANSWERED" collapse, per-emoji reaction chips, per-participant receipts.
 * @param {Record<string, string> | null} [props.waGroupNamesByJid] Lowercased participant jid/lid →
 *   roster display name, for reaction tooltips and the receipts popover. Group threads only.
 */
export function MessageClusterList({
  historyLoading,
  historyError,
  historyItemsLength,
  historyEndRef,
  messageClusters,
  isOwnMessage,
  isBotSender,
  getSenderLabel,
  getSenderInitials,
  getSenderAvatarUrl,
  selectedThreadId,
  patientUserId,
  onToggleReaction,
  onForwardMessage,
  // Multi-select forwarding: hands the whole selection up (conversation order) so the page can
  // open the forward dialog once for the batch. Absent hides the "Select messages…" entry.
  onForwardMessages,
  // Bumped by the page after a batch actually went out — clears the selection. A number rather
  // than a boolean so a cancelled dialog leaves the selection intact.
  forwardSelectionResetToken = 0,
  // "Delete for everyone" — WhatsApp revoke or internal unsend (the page picks the transport).
  // Absent hides the affordance; it is only ever offered on messages our side sent (WhatsApp) or
  // the author's own (internal).
  onRevokeMessage,
  // "Edit message" — same gating as above, plus the per-scope editability rules in the chip.
  onEditMessage,
  pinnedMessageIds,
  onTogglePin,
  onDeleteMessage,
  onClearMessagesAfter,
  highlightedMessageId,
  // In-chat search: the active term, marked inside every bubble body while the search bar is
  // open. Empty/absent renders exactly as before.
  searchHighlightTerm = "",
  isInternalScope = false,
  mergedView = false,
  getSegmentMeta,
  // Internal Chats: message id the "New messages" divider sits before (null = no divider).
  newMessagesDividerId = null,
  // Internal Chats: member display names whose "@Name" tokens render emphasized.
  mentionNames = null,
  // Delivery/seen receipts. recipientIds is every OTHER thread member — ticks only escalate once
  // all of them have the receipt. Pass null while the roster is still loading: the indicator then
  // falls back to any-recipient rather than stalling. isWhatsAppThread switches to the channel-level
  // status (WhatsApp reports the chat, not per person). memberDirectory resolves ids to names and
  // avatars in the detail popover.
  recipientIds = null,
  primaryRecipientIds = null,
  isWhatsAppThread = false,
  memberDirectory = null,
  // WhatsApp GROUP conversation mode — every 1:1 path below is untouched when false.
  isWaGroupThread = false,
  waGroupNamesByJid = null,
}) {
  const listRef = useRef(null);
  const skeletonRef = useRef(null);
  const wasLoadingRef = useRef(true);
  // Stable per-message keys already on screen, so each newly-arrived message animates exactly once.
  const seenKeysRef = useRef(new Set());
  // The thread the seen-set belongs to; a change means a fresh population (reset, don't animate).
  const seenThreadRef = useRef(null);
  const [fadingSkeleton, setFadingSkeleton] = useState(false);
  // Transient ring on a quoted message the user jumped to by clicking its quote block.
  const [jumpHighlightId, setJumpHighlightId] = useState(null);
  const jumpHighlightTimerRef = useRef(null);
  // The multi-select bar. null = idle; otherwise { mode, keys } — ONE mode at a time, so the two
  // flavours can never fight over the gutter, the Escape key or the toolbar slot.
  // The granularity genuinely differs and must stay apart:
  //   "copy"    keys are BUBBLE keys (data-copy-bubble-key) — the capture is per <article>.
  //   "forward" keys are per-MESSAGE part keys (data-part-key) — a forward is one message, and a
  //             merged bubble holds several.
  const [selection, setSelection] = useState(
    /** @type {{ mode: "copy" | "forward", keys: string[] } | null} */ (null),
  );
  const [copyBusy, setCopyBusy] = useState(false);
  const copySelecting = selection?.mode === "copy";
  const forwardSelecting = selection?.mode === "forward";
  const selectedCount = selection?.keys.length ?? 0;

  const isHighlighted = (id) =>
    (highlightedMessageId != null && String(id) === String(highlightedMessageId)) ||
    (jumpHighlightId != null && String(id) === String(jumpHighlightId));

  // Internal Chats @mentions render bold: wrap known members' "@Name" tokens in ** so the
  // existing markdown pipeline emphasizes them — no custom renderer. Longest names first
  // so "@Anna Nowak" wins over a member named "Anna".
  const emphasizeMentions = (text) => {
    if (!isInternalScope || !Array.isArray(mentionNames) || mentionNames.length === 0) return text;
    const value = String(text || "");
    if (!value.includes("@")) return value;
    let out = value;
    const names = [...mentionNames].filter(Boolean).sort((a, b) => b.length - a.length);
    for (const name of names) {
      const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      out = out.replace(new RegExp(`@${escaped}(?![\\w])`, "g"), `**@${name}**`);
    }
    return out;
  };

  // The search term, but only for bodies that actually contain it. Handing the term to every
  // MessageBodyMarkdown would invalidate all of them on each new search and re-run the markdown
  // parse for the whole conversation; this way only the matching bubbles do any work, and the rest
  // keep a stable "" prop and stay memoized.
  const highlightFor = (text) => {
    if (!searchHighlightTerm) return "";
    const body = String(text || "");
    if (!body) return "";
    return body.toLowerCase().includes(searchHighlightTerm.toLowerCase()) ? searchHighlightTerm : "";
  };

  // Deselecting the last entry leaves the mode entirely — the bar has nothing left to act on.
  const toggleSelect = (mode, rawKey) => {
    const key = String(rawKey || "").trim();
    if (!key) return;
    // The cap is checked HERE and not inside the updater below: React re-invokes updaters (twice
    // in StrictMode), so a toast fired from in there would double up. Reading the current
    // selection from the closure is safe — these are user clicks, one render apart.
    if (
      mode === "forward" &&
      selection?.mode === "forward" &&
      !selection.keys.includes(key) &&
      selection.keys.length >= MAX_FORWARD_SELECTION
    ) {
      toast.error(`You can forward at most ${MAX_FORWARD_SELECTION} messages at once`);
      return;
    }
    setSelection((prev) => {
      if (prev == null || prev.mode !== mode) return { mode, keys: [key] };
      if (prev.keys.includes(key)) {
        const keys = prev.keys.filter((k) => k !== key);
        return keys.length ? { mode, keys } : null;
      }
      return { mode, keys: [...prev.keys, key] };
    });
  };

  const toggleCopySelect = (bubbleKey) => toggleSelect("copy", bubbleKey);
  const toggleForwardSelect = (partKey) => toggleSelect("forward", partKey);

  // Clicking the message body toggles it, like the bubble-level copy handler — but stops there,
  // so a merged bubble's parts stay individually selectable.
  const selectPartOnClick = (partKey) => (event) => {
    if (event.target.closest("button, a, input, textarea, [data-capture-ignore]")) return;
    event.stopPropagation();
    toggleForwardSelect(partKey);
  };

  const clearSelection = () => {
    setSelection(null);
  };

  // Entering preselects the message the menu was opened on, and replaces any copy selection.
  const startForwardSelection = (message) => {
    const key = message?.clientId || message?.id || getMessageDedupKey(message);
    if (!key) return;
    setSelection({ mode: "forward", keys: [String(key)] });
  };

  const confirmCopySelectionAsImage = async () => {
    const root = listRef.current;
    const keys = copySelecting ? selection.keys : null;
    if (!root || !keys?.length || copyBusy) return;
    const selected = new Set(keys);
    const articles = Array.from(root.querySelectorAll("article[data-copy-bubble-key]")).filter((el) =>
      selected.has(el.getAttribute("data-copy-bubble-key") || ""),
    );
    if (!articles.length) {
      toast.error("No messages selected");
      return;
    }
    setCopyBusy(true);
    try {
      const blob = await captureElementAsPngBlob(articles);
      await copyImageBlobToClipboard(blob);
      toast.success(articles.length === 1 ? "Message copied as image" : `${articles.length} messages copied as image`);
      setSelection(null);
    } catch (err) {
      console.error("[copy-message-as-image]", err);
      toast.error("Couldn't copy message as image");
    } finally {
      setCopyBusy(false);
    }
  };

  useEffect(() => {
    setSelection(null);
  }, [selectedThreadId]);

  // The batch left for the destination — the selection has done its job.
  useEffect(() => {
    setSelection(null);
  }, [forwardSelectionResetToken]);

  useEffect(() => {
    if (!selection) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setSelection(null);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selection]);

  // WhatsApp message id → message, for resolving native reply-to quotes
  // (`metadata.whatsapp.quotedMessageId`) against the loaded history.
  const waMessageIndex = useMemo(
    () => buildWhatsAppMessageIdIndex(messageClusters),
    [messageClusters],
  );

  // part key → message, walked in conversation order. A Map keeps insertion order, so reading it
  // back yields the conversation order regardless of the order the user ticked things. Covers the
  // merged WhatsApp view too: renderMergedSegments re-groups these same clusters.
  const messagesByPartKey = useMemo(() => {
    const index = new Map();
    for (const cluster of messageClusters || []) {
      if (cluster?.type !== "chat") continue;
      for (const msg of cluster.items || []) {
        const key = msg?.clientId || msg?.id || getMessageDedupKey(msg);
        if (key) index.set(String(key), msg);
      }
    }
    return index;
  }, [messageClusters]);

  const handleForwardSelection = () => {
    if (!forwardSelecting || !onForwardMessages) return;
    const picked = new Set(selection.keys);
    const messages = [];
    for (const [key, msg] of messagesByPartKey) {
      if (picked.has(key)) messages.push(msg);
    }
    if (!messages.length) {
      toast.error("No messages selected");
      return;
    }
    onForwardMessages(messages);
  };

  const jumpToMessage = (messageId) => {
    const root = listRef.current;
    if (!root || !messageId) return;
    const el = root.querySelector(`[data-msg-id="${CSS.escape(String(messageId))}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setJumpHighlightId(String(messageId));
    if (jumpHighlightTimerRef.current) clearTimeout(jumpHighlightTimerRef.current);
    jumpHighlightTimerRef.current = setTimeout(() => setJumpHighlightId(null), 1800);
  };

  const renderQuotedBlock = (msg) => {
    const quotedId = getWhatsAppQuotedMessageId(msg);
    if (!quotedId) return null;
    return (
      <QuotedMessageBlock
        quotedMessage={waMessageIndex.get(quotedId) || null}
        getSenderLabel={getSenderLabel}
        isBotSender={isBotSender}
        onJumpToMessage={jumpToMessage}
      />
    );
  };

  // Forward provenance is an internal-chats affordance only: a patient's conversation renders a
  // forwarded copy exactly like any other message ("forwarded will not be visible").
  const renderForwardedMarker = (msg) => {
    if (!isInternalScope) return null;
    const info = getForwardedInfo(msg);
    if (!info) return null;
    return <ForwardedMarkerBlock info={info} />;
  };

  useLayoutEffect(() => {
    const recordAllKeys = () => {
      listRef.current?.querySelectorAll("[data-part-key]").forEach((el) => {
        const key = el.getAttribute("data-part-key");
        if (key) seenKeysRef.current.add(key);
      });
    };

    if (historyLoading) {
      wasLoadingRef.current = true;
      seenKeysRef.current.clear();
      seenThreadRef.current = selectedThreadId;
      setFadingSkeleton(false);
      return;
    }

    // Thread switched without a loading flash (e.g. served from cache): treat as a fresh
    // population — adopt its messages as already-seen so none of them animate in.
    if (seenThreadRef.current !== selectedThreadId) {
      wasLoadingRef.current = false;
      seenThreadRef.current = selectedThreadId;
      seenKeysRef.current.clear();
      recordAllKeys();
      return;
    }

    if (wasLoadingRef.current && messageClusters.length > 0) {
      wasLoadingRef.current = false;
      setFadingSkeleton(true);
      // The initial reveal is the skeleton-stagger effect below; don't double-animate as "new".
      recordAllKeys();
      return;
    }

    wasLoadingRef.current = false;

    const root = listRef.current;
    if (!root) return;

    // Every message carries a stable data-part-key. A new key at the very TOP means older history
    // was just prepended (load-older) — skip the whole batch so scrolling up doesn't replay the
    // past. Otherwise animate every new key wherever it sits: fresh sends/replies and merged
    // same-sender segments alike, so *every* message animates exactly once.
    const parts = Array.from(root.querySelectorAll("[data-part-key]"));
    const firstKey = parts[0]?.getAttribute("data-part-key");
    const isPrepend =
      seenKeysRef.current.size > 0 && firstKey != null && !seenKeysRef.current.has(firstKey);

    const fresh = isPrepend
      ? []
      : parts.filter((el) => {
          const key = el.getAttribute("data-part-key");
          return key != null && !seenKeysRef.current.has(key);
        });

    parts.forEach((el) => {
      const key = el.getAttribute("data-part-key");
      if (key) seenKeysRef.current.add(key);
    });

    // Own messages written here fly from the composer (driven by the page hook), so skip their
    // in-place entrance to avoid two animations fighting over the same node.
    const toAnimate = fresh
      .filter((el) => !el.hasAttribute("data-optimistic-id"))
      .map(entranceAnimationTarget);
    if (toAnimate.length > 0) {
      // Promote to their own layers so the transform/opacity tween stays smooth; release after.
      toAnimate.forEach((el) => {
        el.style.willChange = "transform, opacity";
      });
      animate(toAnimate, {
        translateY: [14, 0],
        opacity: [0, 1],
        scale: [0.98, 1],
        duration: 340,
        delay: toAnimate.length > 1 ? stagger(26) : 0,
        ease: "outCubic",
        onComplete: () => {
          toAnimate.forEach((el) => {
            el.style.willChange = "";
          });
        },
      });
    }
  }, [historyLoading, messageClusters, selectedThreadId]);

  useLayoutEffect(() => {
    if (!fadingSkeleton) return;

    if (skeletonRef.current) {
      animate(skeletonRef.current, {
        opacity: [1, 0],
        scale: [1, 0.97],
        duration: 500,
        ease: "inOutCubic",
      });
    }

    if (listRef.current) {
      const allBubbles = listRef.current.querySelectorAll("[data-msg-bubble]");
      if (allBubbles.length > 0) {
        const arr = Array.from(allBubbles);
        const toAnimate = arr.length > MAX_STAGGER_BUBBLES ? arr.slice(-MAX_STAGGER_BUBBLES) : arr;
        animate(toAnimate, {
          translateY: [14, 0],
          duration: 480,
          delay: stagger(18),
          ease: "outCubic",
        });
      }
    }

    const timer = setTimeout(() => setFadingSkeleton(false), 520);
    return () => clearTimeout(timer);
  }, [fadingSkeleton]);

  const showMessages = !historyLoading && !historyError && messageClusters.length > 0;
  const showSkeleton = historyLoading || fadingSkeleton;

  if (historyError && !historyLoading) {
    return (
      <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
        {historyError}
      </div>
    );
  }

  if (!historyLoading && !fadingSkeleton && !historyError && historyItemsLength === 0) {
    return <div className="flex h-full items-center justify-center text-sm text-muted-foreground">No history entries</div>;
  }

  // Render one cluster row (auto/call/system notice or a chat bubble group). Extracted so both the
  // plain list and the merged-view segments can render the exact same rows. `showBadge` adds the
  // per-clinic avatar badge on inbound rows (merged view only); it reads the segment's `--seg-solid`
  // CSS var set on the enclosing segment root.
  const renderCluster = (cluster, cIndex, { showBadge = false } = {}) => {
            if (cluster.type === "auto") {
              const item = cluster.item;
              const rowKey = item.clientId || item.id || getMessageDedupKey(item);
              return (
                <div
                  key={rowKey}
                  data-msg-bubble
                  data-msg-id={item.id || undefined}
                  data-part-key={rowKey}
                  className={`flex w-full justify-center ${cIndex === 0 ? "mt-0" : "mt-3"}`}
                >
                  <div
                    role="status"
                    className={cn(
                      "group/part relative max-w-[min(92%,44rem)] rounded-xl border border-amber-500/40 bubble-surface-amber px-4 py-3 text-start dark:border-amber-400/35",
                      isHighlighted(item.id) && HIGHLIGHT_RING_CLASS,
                    )}
                  >
                    <span className="sr-only">Auto reply: </span>
                    <MessageBodyMarkdown className="leading-relaxed text-amber-900 dark:text-amber-100">
                      {getMessageText(item) || "Please wait while we process your message."}
                    </MessageBodyMarkdown>
                    <MessagePartActions
                      message={item}
                      threadId={selectedThreadId}
                      onDeleteMessage={onDeleteMessage}
                      onClearMessagesAfter={onClearMessagesAfter}
                    />
                  </div>
                </div>
              );
            }
            if (cluster.type === "call") {
              const item = cluster.item;
              const rowKey = item.clientId || item.id || getMessageDedupKey(item);
              const detail = getCallLogDetail(item) || {};
              const needsAttention =
                detail.direction !== "outgoing" &&
                (detail.outcome === "missed" || detail.outcome === "declined");
              const CallIcon = detail.isVideo
                ? Video
                : detail.direction === "outgoing"
                  ? PhoneOutgoing
                  : detail.outcome === "answered"
                    ? PhoneIncoming
                    : detail.outcome === "declined"
                      ? PhoneOff
                      : detail.outcome === "missed"
                        ? PhoneMissed
                        : Phone;
              const callTime = getMessageTime(item);
              return (
                <div
                  key={rowKey}
                  data-msg-bubble
                  data-msg-id={item.id || undefined}
                  data-part-key={rowKey}
                  className={`flex w-full justify-center ${cIndex === 0 ? "mt-0" : "mt-3"}`}
                >
                  <div
                    role="status"
                    className={cn(
                      "group/part relative flex max-w-[min(92%,44rem)] items-center gap-2 rounded-full border px-4 py-2 text-sm",
                      needsAttention
                        ? "border-rose-500/40 bg-rose-500/10 text-rose-700 dark:border-rose-400/35 dark:text-rose-300"
                        : "border-border/60 bubble-surface-muted text-muted-foreground",
                      isHighlighted(item.id) && HIGHLIGHT_RING_CLASS,
                    )}
                  >
                    <span className="sr-only">Call: </span>
                    <CallIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                    <span className="font-medium">
                      {getMessageText(item) || "Call"}
                    </span>
                    {/* Only a real positive duration: several platforms deliver the accept to
                        linked devices at teardown only, so an answered call can arrive with a
                        bogus 0s — showing nothing beats showing "0s". */}
                    {detail.durationSeconds > 0 && (
                      <span className="opacity-80">· {formatCallDuration(detail.durationSeconds)}</span>
                    )}
                    {callTime && <span className="text-xs opacity-70">{callTime}</span>}
                    <MessagePartActions
                      message={item}
                      threadId={selectedThreadId}
                      onDeleteMessage={onDeleteMessage}
                      onClearMessagesAfter={onClearMessagesAfter}
                    />
                  </div>
                </div>
              );
            }
            if (cluster.type === "system") {
              const item = cluster.item;
              const rowKey = item.clientId || item.id || getMessageDedupKey(item);
              return (
                <div
                  key={rowKey}
                  data-msg-bubble
                  data-msg-id={item.id || undefined}
                  data-part-key={rowKey}
                  className={`flex w-full justify-center ${cIndex === 0 ? "mt-0" : "mt-3"}`}
                >
                  <div
                    role="status"
                    className={cn(
                      "group/part relative max-w-[min(90%,42rem)] rounded-lg border border-dashed border-border/60 bubble-surface-muted px-4 py-3 text-start",
                      isHighlighted(item.id) && HIGHLIGHT_RING_CLASS,
                    )}
                  >
                    <span className="sr-only">System notice: </span>
                    <MessageBodyMarkdown className="message-markdown--system leading-relaxed">
                      {getMessageText(item)}
                    </MessageBodyMarkdown>
                    <MessagePartActions
                      message={item}
                      threadId={selectedThreadId}
                      onDeleteMessage={onDeleteMessage}
                      onClearMessagesAfter={onClearMessagesAfter}
                    />
                  </div>
                </div>
              );
            }

            const items = cluster.items;
            const firstItem = items[0];
            const lastItem = items[items.length - 1];
            const ownMessage = isOwnMessage(firstItem);
            const botMessage = isBotSender(firstItem);
            const staffAnswerMessage = isWhatsAppStaffAnswerMessage(firstItem);
            const useCompactOwnLayout = ownMessage && !staffAnswerMessage;
            const rightAlignedMessage = useCompactOwnLayout || botMessage || staffAnswerMessage;
            // WhatsApp only lets an account retract/edit its OWN messages, and right-aligned is
            // exactly "left our side" — our send, the bot's reply, or a staff answer from the linked
            // phone. Internal Chats and Healo-app patient conversations ("app": anything that is
            // not WhatsApp): only the AUTHOR's own messages (the server re-checks authorship).
            const actionScope = isWhatsAppThread ? "whatsapp" : isInternalScope ? "internal" : "app";
            const clusterActionsAllowed = actionScope === "whatsapp" ? rightAlignedMessage : ownMessage;
            const clusterRevokeHandler = clusterActionsAllowed ? onRevokeMessage : undefined;
            const clusterEditHandler = clusterActionsAllowed ? onEditMessage : undefined;
            // A teammate's reply can land on the LEFT next to the patient's inbound messages — it did
            // not go out as a WhatsApp `fromMe` staff answer, nor is it the current user's own send.
            // When we know which participant is the patient, tint those teammate bubbles like a staff
            // answer so a colleague's message is never mistaken for the patient's at a glance. Skipped
            // for internal (staff-to-staff) chats, which have no patient to contrast against.
            const senderId = String(firstItem?.sender?.userId || "").trim();
            const normalizedPatientUserId = String(patientUserId || "").trim();
            const staffLeftMessage =
              !isInternalScope &&
              !ownMessage &&
              !botMessage &&
              !staffAnswerMessage &&
              !!normalizedPatientUserId &&
              !!senderId &&
              senderId !== normalizedPatientUserId;
            // Delivery ticks: own messages always; in a WhatsApp thread EVERY outbound message —
            // a teammate's desktop send (which renders LEFT as staffLeftMessage, not as a phone
            // "staff answer"), a bot send, and linked-phone answers alike. The channel status
            // tracks whether the PATIENT received it, which matters to the whole team regardless
            // of author. A recorded channelStatus is outbound evidence on its own (only our sends
            // ever get acks), covering rows where the patient id is unknown. Internal chats stay
            // own-only, matching the usual chat convention.
            const showDeliveryTicks =
              ownMessage ||
              (isWhatsAppThread &&
                (rightAlignedMessage ||
                  staffLeftMessage ||
                  items.some((m) => Boolean(readChannelStatus(m)))));
            const rowKey = items.map((m) => m.clientId || m.id || getMessageDedupKey(m)).join("|");
            // Bubble fills are opaque (bubble-tint-* / bg-card) so the conversation pane's dot
            // texture frames the bubbles instead of bleeding through them.
            const bubbleSkin = useCompactOwnLayout
              ? // Soft drop-shadow makes own bubbles look "detached" / lifted off the background; the
                // send pop scales it in with the bubble (WhatsApp-style).
                "border-primary/15 bubble-tint-own shadow-md shadow-black/10 dark:border-primary/35 dark:shadow-black/40 max-w-[85%]"
              : botMessage
                ? "border-green-500/35 bubble-tint-bot shadow-sm dark:border-green-500/40 dark:shadow-none max-w-[75%]"
                : staffAnswerMessage
                  ? "border-violet-500/35 bubble-tint-staff shadow-sm dark:border-violet-500/40 dark:shadow-none max-w-[75%]"
                  : staffLeftMessage
                    ? "border-indigo-500/35 bubble-tint-teammate shadow-sm dark:border-indigo-500/40 dark:shadow-none max-w-[75%]"
                    : "border-border/70 bg-card shadow-sm dark:border-border dark:bg-card dark:shadow-none max-w-[75%]";

            const copySelected = copySelecting && selection.keys.includes(rowKey);

            return (
              <MessageBubbleAttachmentActionsProvider
                key={rowKey}
                data-msg-bubble
                className={cn(
                  `group/bubble flex items-end gap-2 ${cIndex === 0 ? "mt-0" : "mt-3"} ${rightAlignedMessage ? "justify-end" : "justify-start"}`,
                  copySelecting && "cursor-pointer",
                )}
                onClick={(event) => {
                  if (!copySelecting) return;
                  if (event.target.closest("button, a, input, textarea, [data-capture-ignore]")) return;
                  toggleCopySelect(rowKey);
                }}
              >
                {!rightAlignedMessage && (
                  <div className="relative shrink-0">
                    <Avatar className="h-7 w-7">
                      <AvatarImage src={getSenderAvatarUrl(firstItem)} />
                      {/* Group participants get a stable per-sender hue (seeded by userId) so the
                          many left-aligned senders read distinctly; 1:1 keeps the muted fallback. */}
                      <AvatarFallback
                        className={`text-[10px] ${isBotSender(firstItem) ? "bg-green-500/15 text-green-700 dark:bg-green-500/20 dark:text-green-400" : staffLeftMessage ? "bg-indigo-500/15 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-400" : isWaGroupThread && senderId ? getAvatarColorClasses(senderId) : "bg-muted text-muted-foreground"}`}
                      >
                        {getSenderInitials(firstItem)}
                      </AvatarFallback>
                    </Avatar>
                    {showBadge && (
                      <span
                        className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-(--seg-solid) ring-2 ring-background"
                        aria-hidden
                      />
                    )}
                  </div>
                )}
                <article
                  data-copy-bubble-key={rowKey}
                  className={cn(
                    `relative flow-root min-w-0 rounded-2xl border px-4 py-3 text-start ${rightAlignedMessage ? "w-fit" : "w-full"} ${bubbleSkin}`,
                    copySelected && "ring-2 ring-primary ring-offset-2 ring-offset-background",
                  )}
                >
                  {useCompactOwnLayout ? (
                    items.map((msg, idx) => {
                      const partKey = msg.clientId || msg.id || getMessageDedupKey(msg);
                      const isLastPart = idx === items.length - 1;
                      // A pending WhatsApp edit shows the text the patient is ABOUT to see.
                      const bodyText = emphasizeMentions(getMessageDisplayText(msg));
                      const optimistic = msg.metadata?.optimistic === true;
                      const optimisticFailed = msg.metadata?.optimisticFailed === true;
                      const waDeleted = isWhatsAppDeletedMessage(msg);
                      const forwardSelectable = forwardSelecting && Boolean(msg.id);
                      const forwardSelected = forwardSelectable && selection.keys.includes(partKey);
                      return (
                        <div
                          key={partKey}
                          data-msg-id={msg.id || undefined}
                          data-part-key={partKey}
                          data-optimistic-id={optimistic ? msg.clientId : undefined}
                          onClick={forwardSelectable ? selectPartOnClick(partKey) : undefined}
                          className={cn(
                            "group/part relative transition-opacity",
                            isHighlighted(msg.id) && HIGHLIGHT_RING_CLASS,
                            // No "sending" dim — it would flicker dim→full right after the seamless
                            // travel. Only a failed send is visually marked.
                            optimisticFailed && "opacity-60",
                            waDeleted && "opacity-60",
                            forwardSelectable && "cursor-pointer",
                            forwardSelected && FORWARD_SELECTED_PART_CLASS,
                          )}
                        >
                          {idx > 0 && <div className="my-2.5 border-t border-primary/15 dark:border-primary/25" />}
                          {renderForwardedMarker(msg)}
                          {renderQuotedBlock(msg)}
                          {(bodyText || isLastPart || items.length > 1) && (
                            <div className="wrap-break-word text-sm text-foreground leading-relaxed">
                              {/* Merged bubbles stamp every part; a lone message keeps the single trailing time. */}
                              {(isLastPart || items.length > 1) && (
                                <div className="mt-1 flex items-center justify-end gap-1 whitespace-nowrap text-xs text-muted-foreground">
                                  <EditedMarker message={msg} />
                                  <span>{getMessageTime(msg)}</span>
                                  <MessageReceiptsPopover
                                    message={msg}
                                    isOwn
                                    recipientIds={recipientIds}
                                    primaryRecipientIds={primaryRecipientIds}
                                    isWhatsAppThread={isWhatsAppThread}
                                    memberDirectory={memberDirectory}
                                    isWaGroupThread={isWaGroupThread}
                                    waGroupNamesByJid={waGroupNamesByJid}
                                  />
                                </div>
                              )}
                              {bodyText ? (
                                <MessageBodyMarkdown
                                  className={waDeleted ? "line-through" : undefined}
                                  highlight={highlightFor(bodyText)}
                                >
                                  {bodyText}
                                </MessageBodyMarkdown>
                              ) : null}
                            </div>
                          )}
                          <div className={cn(waDeleted && "opacity-50 grayscale")}>
                            <ChatMessageAttachments attachments={msg.attachments} />
                          </div>
                          {waDeleted ? (
                            <WhatsAppDeletedCue message={msg} className="text-right" />
                          ) : null}
                          <WhatsAppEditCue message={msg} className="text-right" />
                          <MessageReactions
                            message={msg}
                            threadId={selectedThreadId}
                            onToggleReaction={onToggleReaction}
                            rightAligned
                            isInternalScope={isInternalScope}
                            isWaGroupThread={isWaGroupThread}
                            waGroupNamesByJid={waGroupNamesByJid}
                            memberDirectory={memberDirectory}
                          />
                          {forwardSelecting ? (
                            <ForwardSelectPartButton
                              message={msg}
                              partKey={partKey}
                              selected={forwardSelected}
                              onToggle={toggleForwardSelect}
                            />
                          ) : (
                            <MessagePartActions
                              message={msg}
                              threadId={selectedThreadId}
                              onToggleReaction={onToggleReaction}
                              onForwardMessage={onForwardMessage}
                              onStartForwardSelection={onForwardMessages ? startForwardSelection : undefined}
                              onRevokeMessage={clusterRevokeHandler}
                              onEditMessage={clusterEditHandler}
                              actionScope={actionScope}
                              onDeleteMessage={onDeleteMessage}
                              onClearMessagesAfter={onClearMessagesAfter}
                              isOwn
                              recipientIds={recipientIds}
                              primaryRecipientIds={primaryRecipientIds}
                              isWhatsAppThread={isWhatsAppThread}
                              memberDirectory={memberDirectory}
                              isWaGroupThread={isWaGroupThread}
                              waGroupNamesByJid={waGroupNamesByJid}
                            />
                          )}
                          {optimisticFailed ? (
                            <div className="mt-1 text-right text-[11px] font-medium text-destructive">
                              Couldn't send
                            </div>
                          ) : null}
                        </div>
                      );
                    })
                  ) : (
                    <>
                      <div className="flex min-h-4 max-w-full items-start justify-between gap-3">
                        <p
                          className={`max-w-48 truncate text-xs font-semibold uppercase tracking-wide sm:max-w-64 ${staffAnswerMessage
                            ? "text-violet-600 dark:text-violet-400"
                            : staffLeftMessage
                              ? "text-indigo-600 dark:text-indigo-400"
                              : isBotSender(firstItem)
                                ? "text-green-600 dark:text-green-400"
                                : isWaGroupThread && senderId
                                  ? getSenderNameColorClasses(senderId)
                                  : "text-primary"
                            }`}
                        >
                          {/* Groups: many people answer from the business number's linked phones,
                              so collapsing them all to "STAFF ANSWERED" would erase who spoke —
                              show the resolved sender name and let the violet tint keep saying
                              "our side". 1:1 keeps the label collapse. */}
                          {staffAnswerMessage && !isWaGroupThread
                            ? "STAFF ANSWERED"
                            : getSenderLabel(firstItem)}
                        </p>
                        {/* Top-right corner: a lone message keeps its time here (merged bubbles stamp each
                            segment below instead); the pin toggle sits at the very corner. */}
                        <div className="-my-1 ml-auto flex shrink-0 items-center gap-1.5">
                          {items.length === 1 ? (
                            <>
                              <EditedMarker message={lastItem} />
                              <span className="text-xs text-muted-foreground">{getMessageTime(lastItem)}</span>
                            </>
                          ) : null}
                          {/* Outbound messages reach the labelled layout too — a staff WhatsApp
                              answer is never compact (see useCompactOwnLayout), and in WhatsApp
                              threads a teammate's or the bot's send gets ticks as well. */}
                          {showDeliveryTicks && items.length === 1 ? (
                            <MessageReceiptsPopover
                              message={lastItem}
                              isOwn
                              recipientIds={recipientIds}
                              primaryRecipientIds={primaryRecipientIds}
                              isWhatsAppThread={isWhatsAppThread}
                              memberDirectory={memberDirectory}
                              isWaGroupThread={isWaGroupThread}
                              waGroupNamesByJid={waGroupNamesByJid}
                            />
                          ) : null}
                          {forwardSelecting ? null : (
                            <PinMessageButton
                              message={firstItem}
                              isPinned={pinnedMessageIds?.has(String(firstItem.id))}
                              onTogglePin={onTogglePin}
                            />
                          )}
                        </div>
                      </div>
                      {items.map((msg, idx) => {
                        const partKey = msg.clientId || msg.id || getMessageDedupKey(msg);
                        // A pending WhatsApp edit shows the text the patient is ABOUT to see.
                        const bodyText = emphasizeMentions(getMessageDisplayText(msg));
                        const quotedBlock = renderQuotedBlock(msg);
                        const forwardedMarker = renderForwardedMarker(msg);
                        const waDeleted = isWhatsAppDeletedMessage(msg);
                        const forwardSelectable = forwardSelecting && Boolean(msg.id);
                        const forwardSelected = forwardSelectable && selection.keys.includes(partKey);
                        return (
                          <div
                            key={partKey}
                            data-msg-id={msg.id || undefined}
                            data-part-key={partKey}
                            onClick={forwardSelectable ? selectPartOnClick(partKey) : undefined}
                            className={cn(
                              "group/part relative",
                              isHighlighted(msg.id) && HIGHLIGHT_RING_CLASS,
                              waDeleted && "opacity-60",
                              forwardSelectable && "cursor-pointer",
                              forwardSelected && FORWARD_SELECTED_PART_CLASS,
                            )}
                          >
                            {idx === 0 ? (
                              <>
                                {forwardedMarker ? <div className="mt-2.5">{forwardedMarker}</div> : null}
                                {quotedBlock ? (
                                  <div className={forwardedMarker ? undefined : "mt-2.5"}>{quotedBlock}</div>
                                ) : null}
                                {bodyText ? (
                                  <MessageBodyMarkdown
                                    className={cn(
                                      quotedBlock || forwardedMarker ? "wrap-break-word" : "mt-2.5 wrap-break-word",
                                      waDeleted && "line-through",
                                    )}
                                    highlight={highlightFor(bodyText)}
                                  >
                                    {bodyText}
                                  </MessageBodyMarkdown>
                                ) : null}
                                <div className={cn(waDeleted && "opacity-50 grayscale")}>
                                  <ChatMessageAttachments attachments={msg.attachments} />
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="my-2.5 border-t border-border/40 dark:border-border/50" />
                                {forwardedMarker}
                                {quotedBlock}
                                {bodyText ? (
                                  <MessageBodyMarkdown
                                    className={cn("wrap-break-word", waDeleted && "line-through")}
                                    highlight={highlightFor(bodyText)}
                                  >
                                    {bodyText}
                                  </MessageBodyMarkdown>
                                ) : null}
                                <div className={cn(waDeleted && "opacity-50 grayscale")}>
                                  <ChatMessageAttachments attachments={msg.attachments} />
                                </div>
                              </>
                            )}
                            {waDeleted ? <WhatsAppDeletedCue message={msg} /> : null}
                            <WhatsAppEditCue message={msg} />
                            {/* Per-message time on merged bubbles so each stacked reply is dated. */}
                            {items.length > 1 ? (
                              <div className="mt-1 flex items-center justify-end gap-1 whitespace-nowrap text-xs text-muted-foreground">
                                <EditedMarker message={msg} />
                                <span>{getMessageTime(msg)}</span>
                                {showDeliveryTicks ? (
                                  <MessageReceiptsPopover
                                    message={msg}
                                    isOwn
                                    recipientIds={recipientIds}
                                    primaryRecipientIds={primaryRecipientIds}
                                    isWhatsAppThread={isWhatsAppThread}
                                    memberDirectory={memberDirectory}
                                    isWaGroupThread={isWaGroupThread}
                                    waGroupNamesByJid={waGroupNamesByJid}
                                  />
                                ) : null}
                              </div>
                            ) : null}
                            <MessageReactions
                              message={msg}
                              threadId={selectedThreadId}
                              onToggleReaction={onToggleReaction}
                              rightAligned={rightAlignedMessage}
                              isInternalScope={isInternalScope}
                              isWaGroupThread={isWaGroupThread}
                              waGroupNamesByJid={waGroupNamesByJid}
                              memberDirectory={memberDirectory}
                            />
                            {forwardSelecting ? (
                              <ForwardSelectPartButton
                                message={msg}
                                partKey={partKey}
                                selected={forwardSelected}
                                onToggle={toggleForwardSelect}
                              />
                            ) : (
                              <MessagePartActions
                                message={msg}
                                threadId={selectedThreadId}
                                onToggleReaction={onToggleReaction}
                                onForwardMessage={onForwardMessage}
                                onStartForwardSelection={onForwardMessages ? startForwardSelection : undefined}
                                onRevokeMessage={clusterRevokeHandler}
                                onEditMessage={clusterEditHandler}
                                actionScope={actionScope}
                                onDeleteMessage={onDeleteMessage}
                                onClearMessagesAfter={onClearMessagesAfter}
                                isOwn={ownMessage}
                                recipientIds={recipientIds}
                                primaryRecipientIds={primaryRecipientIds}
                                isWhatsAppThread={isWhatsAppThread}
                                memberDirectory={memberDirectory}
                                isWaGroupThread={isWaGroupThread}
                                waGroupNamesByJid={waGroupNamesByJid}
                              />
                            )}
                          </div>
                        );
                      })}
                    </>
                  )}
                  {useCompactOwnLayout && !forwardSelecting ? (
                    // No header on own bubbles — straddle the top-right corner (over the bubble's
                    // padding, not its text) so the pin never covers the message.
                    <PinMessageButton
                      message={firstItem}
                      isPinned={pinnedMessageIds?.has(String(firstItem.id))}
                      onTogglePin={onTogglePin}
                      className="absolute right-0 top-0 z-10 -translate-y-1/2 translate-x-1/2"
                    />
                  ) : null}
                  {/* The copy tick is bubble-scoped and belongs to the OTHER mode — hide it
                      while a forward batch is being picked, so one bubble never shows two
                      different checkboxes meaning two different things. */}
                  {forwardSelecting ? null : (
                    <MessageBubbleGutterActions
                      side={rightAlignedMessage ? "left" : "right"}
                      bubbleKey={rowKey}
                      copySelected={copySelected}
                      copySelecting={copySelecting}
                      onToggleCopySelect={toggleCopySelect}
                    />
                  )}
                </article>

                {rightAlignedMessage && (
                  <Avatar
                    className="h-7 w-7 shrink-0"
                    aria-label={staffAnswerMessage ? "Staff answered" : undefined}
                  >
                    {!staffAnswerMessage && <AvatarImage src={getSenderAvatarUrl(firstItem)} />}
                    <AvatarFallback
                      className={`font-semibold ${staffAnswerMessage
                        ? "bg-violet-500/15 text-violet-700 dark:bg-violet-500/20 dark:text-violet-400"
                        : botMessage
                          ? "text-[10px] bg-green-500/15 text-green-700 dark:bg-green-500/20 dark:text-green-400"
                          : "text-[10px] bg-primary/15 text-primary"
                        }`}
                    >
                      {staffAnswerMessage ? (
                        <StaffHandlesGlyphIcon className="h-[13px] w-[13px] shrink-0" strokeWidth={2.25} aria-hidden />
                      ) : (
                        getSenderInitials(firstItem)
                      )}
                    </AvatarFallback>
                  </Avatar>
                )}
              </MessageBubbleAttachmentActionsProvider>
            );
  };

  // Merged WhatsApp view: fold consecutive clusters from the same clinic thread into one segment
  // with a left gutter rail (vertical clinic label + colored bar). Per-clinic hue comes from the
  // segment root's CSS vars (see merged-thread-colors); descendants read `--seg-*` via Tailwind
  // arbitrary utilities, so the rail, label chip and avatar badges all share one stable color.
  const renderMergedSegments = () => {
    const segments = [];
    for (let idx = 0; idx < messageClusters.length; idx += 1) {
      const cluster = messageClusters[idx];
      const threadId = String(cluster?.threadId || "");
      const prev = segments[segments.length - 1];
      if (prev && prev.threadId === threadId) {
        prev.entries.push({ cluster, cIndex: idx });
      } else {
        segments.push({
          threadId,
          botId: String(cluster?.botId || ""),
          entries: [{ cluster, cIndex: idx }],
        });
      }
    }
    return segments.map((segment, sIndex) => {
      const meta = getSegmentMeta ? getSegmentMeta(segment.botId) : null;
      const shortName = meta?.shortName || meta?.label || "WhatsApp";
      const segKey = `seg-${segment.threadId}-${segment.entries[0]?.cIndex ?? sIndex}`;
      return (
        <div
          key={segKey}
          className={`flex gap-3 ${sIndex === 0 ? "mt-0" : "mt-3"}`}
          style={tenantColorVars(segment.botId)}
        >
          <div className="flex w-11 shrink-0 flex-col items-center gap-1.5 pt-1 sm:w-14">
            <span
              className="rounded-md bg-(--seg-tint) px-1.5 py-1 text-[9px] font-black uppercase leading-none tracking-wider text-(--seg-label) dark:bg-(--seg-tint-dark) dark:text-(--seg-label-dark)"
              style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
              title={meta?.label || shortName}
            >
              {shortName}
            </span>
            <span className="w-1 flex-1 rounded bg-(--seg-solid)" aria-hidden />
          </div>
          <div className="flex min-w-0 flex-1 flex-col">
            {segment.entries.map(({ cluster, cIndex }) =>
              renderCluster(cluster, cIndex, { showBadge: true }),
            )}
          </div>
        </div>
      );
    });
  };

  // The cluster holding the first-unread message: the "New messages" divider renders just
  // above it. Clusters carry either one `item` (auto/call/system) or an `items` group.
  const dividerClusterIndex = (() => {
    const target = String(newMessagesDividerId || "").trim();
    if (!target || mergedView) return -1;
    return messageClusters.findIndex((cluster) => {
      const list = Array.isArray(cluster?.items) ? cluster.items : cluster?.item ? [cluster.item] : [];
      return list.some((m) => String(m?.id || "") === target);
    });
  })();

  const newMessagesDivider = (
    <div
      key="new-messages-divider"
      role="separator"
      aria-label="New messages"
      className="my-3 flex items-center gap-3"
      data-new-messages-divider
    >
      <span className="h-px flex-1 bg-primary/40" aria-hidden />
      <span className="rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
        New messages
      </span>
      <span className="h-px flex-1 bg-primary/40" aria-hidden />
    </div>
  );

  return (
    <div className="relative">
      {showMessages && (
        <div ref={listRef} className="flex flex-col">
          {mergedView
            ? renderMergedSegments()
            : messageClusters.flatMap((cluster, cIndex) => {
                const rendered = renderCluster(cluster, cIndex);
                return cIndex === dividerClusterIndex ? [newMessagesDivider, rendered] : [rendered];
              })}
          <div ref={historyEndRef} />
        </div>
      )}

      {/* One bar for both selection modes — same pill, the primary action names the mode. */}
      {selection ? (
        <div
          data-capture-ignore="true"
          className="sticky bottom-2 z-20 mx-auto mt-3 flex w-fit max-w-full flex-wrap items-center gap-2 rounded-full border border-border/70 bg-background/95 px-3 py-2 shadow-lg backdrop-blur-sm"
          role="toolbar"
          aria-label={forwardSelecting ? "Forward messages" : "Copy messages as image"}
        >
          <span className="px-1 text-xs font-medium text-muted-foreground">
            {selectedCount === 1 ? "1 selected" : `${selectedCount} selected`}
            {forwardSelecting && selectedCount >= MAX_FORWARD_SELECTION ? ` · max ${MAX_FORWARD_SELECTION}` : ""}
          </span>
          {forwardSelecting ? (
            <Button
              type="button"
              size="sm"
              className="h-8 gap-1.5 rounded-full"
              disabled={selectedCount === 0}
              onClick={handleForwardSelection}
            >
              <Forward className="h-3.5 w-3.5" aria-hidden />
              {selectedCount === 1 ? "Forward" : `Forward ${selectedCount}`}
            </Button>
          ) : (
            <Button
              type="button"
              size="sm"
              className="h-8 gap-1.5 rounded-full"
              disabled={copyBusy || selectedCount === 0}
              onClick={() => void confirmCopySelectionAsImage()}
            >
              <Copy className="h-3.5 w-3.5" aria-hidden />
              {copyBusy ? "Copying…" : "Copy as image"}
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 gap-1.5 rounded-full"
            disabled={copyBusy}
            onClick={clearSelection}
          >
            <X className="h-3.5 w-3.5" aria-hidden />
            Cancel
          </Button>
        </div>
      ) : null}

      {showSkeleton && (
        <div
          ref={skeletonRef}
          className={cn(
            fadingSkeleton && !historyLoading && "absolute inset-x-0 top-0 z-10 pointer-events-none",
          )}
        >
          <LoadingSkeleton />
        </div>
      )}
    </div>
  );
}
